import { shallowEqual, useSelector } from 'react-redux';
import type { Table } from 'schema-dts';
import { toCurrencyTruncated } from '@/utilities/formatting';
import type { State as CompanyState } from '@/pages/CompanyReport/redux/interface';
import DocumentSchema from '@/components/DocumentSchema';
import { getStabilityAndGrowthOfPaymentsRawDataSelector } from '@/pages/CompanyReport/components/Dividend/components/StabilityAndGrowthOfPayments/redux/selectors';
import { StabilityAndGrowthTable, StabilityAndGrowthFutureRow } from './styled';
import type { ViewMode } from '@/pages/CompanyReport/enum';
import ListTruncator from '@/components/ListTruncator';
import { useDividendsData } from '../StabilityAndGrowthOfPayments/hooks/useDividendsData';
import { getCurrencyISOCode } from '../StabilityAndGrowthOfPayments/containers/utils';
import { formatterCurrencyTruncated } from '@/utilities/format';
import { useDividendTranslation } from '../../hooks/useDividendTranslations';
import { useLocale } from '@/hooks/useLocale';
import { useDateFormatter } from '@/hooks/useDateFormatter';

interface Props {
  companyName: string;
  companyId: string;
  viewMode: ViewMode;
}
interface ReduxState {
  company: CompanyState;
}

function selector(state: ReduxState) {
  const rawData = getStabilityAndGrowthOfPaymentsRawDataSelector(state, {});

  return {
    futureDividendEstimates: rawData.futureDividendEstimates,
  };
}

export const StabilityAndGrowthOfPaymentsTable = ({
  companyName,
  companyId,
  viewMode,
}: Props) => {
  const { futureDividendEstimates } = useSelector<
    Parameters<typeof selector>[0],
    ReturnType<typeof selector>
  >(selector, shallowEqual);

  /**
   * Lift translation up if needed
   */
  const t = useDividendTranslation();
  const { format } = useDateFormatter();
  const locale = useLocale();
  const caption = t('stabilityAndGrowth.table.caption', {
    company: companyName,
  });
  const { data } = useDividendsData({
    id: companyId,
  });
  const dividends = data?.dividends ?? [];
  const totalRows = futureDividendEstimates.length + dividends.length;
  const currencyISO = getCurrencyISOCode(data);

  const Table = (
    <>
      <DocumentSchema<Table>
        item={{
          '@context': 'https://schema.org',
          '@type': 'Table',
          headline: t('stabilityAndGrowth.table.heading', {
            company: companyName,
          }),
          about: caption,
        }}
      />
      <StabilityAndGrowthTable
        columnTextAlignments="l"
        data-cy-id="dividend-stability-and-growth-of-payments-table"
      >
        <caption data-cy-id="dividend-stability-and-growth-of-payments-table-caption">
          {caption}
        </caption>
        <thead>
          <tr>
            <th>{t('stabilityAndGrowth.table.heading1')}</th>
            <th>{t('stabilityAndGrowth.table.heading2')}</th>
            <th>{t('stabilityAndGrowth.table.heading3')}</th>
            <th>{t('stabilityAndGrowth.table.heading4')}</th>
          </tr>
        </thead>
        <tbody>
          {futureDividendEstimates.map((future, i) => (
            <StabilityAndGrowthFutureRow
              data-cy-id={`future-dividend-stability-and-growth-of-payments-table-row-${i}`}
              key={`future-estimates-row-${i}`}
            >
              <td>{format(future.dateKey, 'MM/dd/yyyy')}</td>
              <td>
                {future.data[0] === null || future.data[0] === 0
                  ? t('n/a')
                  : toCurrencyTruncated(future.data[0], 3, currencyISO)}
              </td>
              <td>
                {future.data[1] === null
                  ? t('n/a')
                  : (future.data[1] / 100).toLocaleString(locale, {
                      style: 'percent',
                      minimumFractionDigits: 2,
                    })}
              </td>
              <td>{t('n/a')}</td>
            </StabilityAndGrowthFutureRow>
          ))}
          {dividends.map(
            (dividend, i) =>
              dividend.exDate !== null && (
                <tr
                  data-cy-id={`past-dividend-stability-and-growth-of-payments-table-row-${i}`}
                  key={`past-estimates-row-${i}`}
                >
                  <td>{format(dividend.exDate, 'MM/dd/yyyy')}</td>
                  <td>
                    {dividend.annualizedDividend === 0
                      ? t('n/a')
                      : formatterCurrencyTruncated(
                          dividend.annualizedDividend,
                          currencyISO,
                          3,
                          t('n/a')
                        )}
                  </td>
                  <td>
                    {dividend.annualizedYield !== null
                      ? (dividend.annualizedYield / 100).toLocaleString(
                          locale,
                          { style: 'percent', minimumFractionDigits: 2 }
                        )
                      : t('n/a')}
                  </td>
                  <td>
                    {formatterCurrencyTruncated(
                      dividend.divAmount,
                      currencyISO,
                      3,
                      t('n/a')
                    )}
                  </td>
                </tr>
              )
          )}
        </tbody>
      </StabilityAndGrowthTable>
    </>
  );

  return totalRows > 9 ? (
    <div data-cy-id="dividend-stability-and-growth-of-payments-truncator">
      <ListTruncator viewMode={viewMode} minHeight={430}>
        {Table}
      </ListTruncator>
    </div>
  ) : (
    Table
  );
};
